// pages/public/BuscarReserva.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, User, IdCard, Calendar, MapPin, Clock, DollarSign, CheckCircle, XCircle, Clock as ClockIcon, Phone, Mail, Download } from 'lucide-react';
import PublicLayout from '../../layouts/PublicLayout';
import { adminAPI } from '../../services/api';
import { pdf } from '@react-pdf/renderer';
import ComprobantePDF from '../../components/ComprobantePDF';
import { useDivisas } from '../../contexts/DivisasContext';
import Swal from 'sweetalert2';

const API_URL = process.env.REACT_APP_API_URL;

const BuscarReserva = () => {
  const navigate = useNavigate();
  const { convertir, formatNumero } = useDivisas();
  const [cedula, setCedula] = useState('');
  const [loading, setLoading] = useState(false);
  const [reserva, setReserva] = useState(null);
  const [error, setError] = useState(null);
  const [descargando, setDescargando] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!cedula || cedula.trim() === '') {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Ingresa una cédula para buscar',
        confirmButtonColor: '#e83e8c'
      });
      return;
    }

    setLoading(true);
    setError(null);
    setReserva(null);

    try {
      const data = await adminAPI.buscarReservaPorCedula(cedula.trim());
      
      if (data.notFound) {
        setError('No se encontró ninguna reserva activa con esa cédula');
      } else {
        let monedaMetodoPago = null;
        try {
          const metodosResponse = await fetch(
            `${API_URL}/servicios/${data.servicio.id}/metodos-pago`
          );
          if (metodosResponse.ok) {
            const metodos = await metodosResponse.json();
            const metodo = metodos.find(m => m.nombre === data.metodoPago);
            monedaMetodoPago = metodo?.moneda || null;
          }
        } catch (err) {
          console.error('Error al obtener moneda del método de pago:', err);
        }
        
        setReserva({ ...data, monedaMetodoPago });
      }
    } catch (error) {
      console.error('Error al buscar reserva:', error);
      setError('Error al buscar la reserva. Intentá nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const calcularDatosViaje = () => {
    if (!reserva) return null;

    if (reserva.tramoIds && reserva.servicio.tramos && reserva.servicio.tramos.length > 0) {
      const idsSeleccionados = reserva.tramoIds
        .split(',')
        .map(id => parseInt(id.trim(), 10))
        .filter(id => !isNaN(id));

      const tramosSeleccionados = reserva.servicio.tramos
        .filter(tramo => idsSeleccionados.includes(tramo.id))
        .sort((a, b) => a.orden - b.orden);

      if (tramosSeleccionados.length > 0) {
        const primerTramo = tramosSeleccionados[0];
        const ultimoTramo = tramosSeleccionados[tramosSeleccionados.length - 1];

        return {
          origen: primerTramo.origen,
          destino: ultimoTramo.destino,
          fecha: primerTramo.fechaSalida || reserva.servicio.fecha,
          horaSalida: primerTramo.horaSalida?.substring(0, 5),
          horaLlegada: ultimoTramo.horaLlegada?.substring(0, 5),
          esPorTramos: true,
        };
      }
    }

    return {
      origen: reserva.servicio.origen,
      destino: reserva.servicio.destino,
      fecha: reserva.servicio.fecha,
      horaSalida: reserva.servicio.horaSalida?.substring(0, 5),
      horaLlegada: reserva.servicio.horaLlegada?.substring(0, 5),
      esPorTramos: false,
    };
  };

  const construirServicioParaPDF = () => {
    if (!reserva) return null;
    const datos = calcularDatosViaje();
    return {
      ...reserva.servicio,
      horario: {
        origen: datos.origen,
        destino: datos.destino,
        salida: datos.horaSalida,
        llegada: datos.horaLlegada,
      },
      precio: reserva.total / reserva.cantidadPasajeros,
      fecha: datos.fecha,
    };
  };

  const construirTitularParaPDF = () => {
    if (!reserva) return null;
    return {
      nombre: reserva.nombrePasajero,
      cedula: reserva.cedulaTitular || '',
      telefonoWhatsapp: reserva.telefonoPasajero,
      telefonoParticular: reserva.telefonoParticular || '',
    };
  };

  const construirPasajerosParaPDF = () => {
    if (!reserva) return [];
    const nombres = reserva.nombresPasajeros
      ? reserva.nombresPasajeros.split(',').map(n => n.trim()).filter(n => n !== '')
      : [];
    const cedulas = reserva.cedulasPasajeros
      ? reserva.cedulasPasajeros.split(',').map(c => c.trim())
      : [];
    const adicionales = [];
    for (let i = 1; i < nombres.length; i++) {
      const cedula = cedulas[i - 1] || '';
      const esMenor = cedula === 'Menor de edad' || cedula === '';
      adicionales.push({
        nombre: nombres[i],
        cedula: esMenor ? '' : cedula,
        esMenor: esMenor,
      });
    }
    return adicionales;
  };

  const descargarPDF = async () => {
    if (!reserva) return;
    setDescargando(true);
    try {
      const servicioPDF = construirServicioParaPDF();
      const titularPDF = construirTitularParaPDF();
      const pasajerosPDF = construirPasajerosParaPDF();

      const reservaPDF = {
        codigo: reserva.codigoUnico,
        total: reserva.total,
      };

      const conversion = convertir(reserva.total);
      const montoConvertido =
        reserva.monedaMetodoPago === 'VES'
          ? conversion.bolivares
          : reserva.monedaMetodoPago === 'BRL'
            ? conversion.reales
            : null;

      const blob = await pdf(
        <ComprobantePDF
          reserva={reservaPDF}
          servicio={servicioPDF}
          titular={titularPDF}
          pasajerosAdicionales={pasajerosPDF}
          metodoPago={reserva.metodoPago}
          comprobante={reserva.comprobante || ''}
          cantidadPasajeros={reserva.cantidadPasajeros}
          monedaMetodoPago={reserva.monedaMetodoPago}
          montoConvertido={montoConvertido}
        />
      ).toBlob();
      
      if (!blob) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo generar el comprobante',
          confirmButtonColor: '#e83e8c'
        });
        return;
      }
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `comprobante-${reserva.codigoUnico}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('❌ Error al descargar PDF:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo descargar el comprobante',
        confirmButtonColor: '#e83e8c'
      });
    } finally {
      setDescargando(false);
    }
  };

  const formatPrecio = (precio) => {
    return new Intl.NumberFormat('es-AR').format(precio);
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'Sin fecha';
    return new Date(fecha).toLocaleDateString('es-AR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'Pendiente': return 'bg-yellow-100 text-yellow-800';
      case 'Pagado': return 'bg-green-100 text-green-800';
      case 'Cancelado': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEstadoIcon = (estado) => {
    switch (estado) {
      case 'Pendiente': return <ClockIcon className="w-4 h-4" />;
      case 'Pagado': return <CheckCircle className="w-4 h-4" />;
      case 'Cancelado': return <XCircle className="w-4 h-4" />;
      default: return null;
    }
  };

  const getLabelTotal = () => {
    if (reserva?.monedaMetodoPago === 'VES') return 'Total pagado';
    if (reserva?.monedaMetodoPago === 'BRL') return 'Total pagado';
    return 'Total';
  };

  const datosViaje = calcularDatosViaje();
  const conversion = convertir(reserva?.total || 0);

  return (
    <PublicLayout>
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-light text-gray-700 mb-2">Consultar mi reserva</h2>
          <p className="text-gray-500 text-sm">Ingresa tu cédula para ver los detalles de tu viaje</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <form onSubmit={handleSubmit} className="p-6">
              <div className="relative">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                  Cédula del titular
                </label>
                <div className="relative">
                  <IdCard className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#e83e8c] w-5 h-5" />
                  <input
                    type="text"
                    value={cedula}
                    onChange={(e) => setCedula(e.target.value)}
                    placeholder="Ej: 12.345.678-9"
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:border-[#e83e8c] focus:ring-2 focus:ring-[#e83e8c]/20 focus:outline-none transition-all text-sm bg-gray-50 hover:bg-white"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full mt-4 bg-[#e83e8c] hover:bg-[#c2185b] text-white py-3 rounded-xl text-sm font-medium transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white"></div>
                  ) : (
                    <>
                      <Search className="w-4 h-4" />
                      Buscar reserva
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 flex flex-col justify-center">
            <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3 text-center">
              ¿Necesitas ayuda?
            </h4>
            <div className="flex flex-col items-center gap-2 text-sm">
              <div className="flex items-center gap-2 text-gray-700">
                <Phone className="w-4 h-4 text-[#e83e8c]" />
                <span><strong>Teléfono:</strong> +584145521017</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <Mail className="w-4 h-4 text-[#e83e8c]" />
                <span><strong>Email:</strong> soporte@transportetrenalsur.com</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Horario de atención: Lun a Vie de 8:00 a 18:00 hs
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {reserva && datosViaje && (
          <div className="mt-6 bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <div className="p-6">
              <div className={`mb-4 p-3 rounded-lg border ${
                reserva.estado === 'Pendiente' 
                  ? 'bg-yellow-50 border-yellow-200' 
                  : reserva.estado === 'Pagado' 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
              }`}>
                <p className={`text-sm text-center font-medium ${
                  reserva.estado === 'Pendiente' 
                    ? 'text-yellow-800' 
                    : reserva.estado === 'Pagado' 
                      ? 'text-green-800' 
                      : 'text-red-800'
                }`}>
                  {reserva.estado === 'Pendiente' && (
                    <>⚠️ Esta reserva está <strong>pendiente de pago</strong>. Recordá pagar en caja 1 hora antes de la salida.</>
                  )}
                  {reserva.estado === 'Pagado' && (
                    <>✅ Esta reserva está <strong>confirmada y pagada</strong>. Presentá tu documento al abordar.</>
                  )}
                  {reserva.estado === 'Cancelado' && (
                    <>❌ Esta reserva ha sido <strong>cancelada</strong>.</>
                  )}
                </p>
              </div>

              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Detalles de la reserva</h3>
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${getEstadoColor(reserva.estado)}`}>
                  {getEstadoIcon(reserva.estado)}
                  {reserva.estado}
                </span>
              </div>

              <div className="bg-pink-50 rounded-xl p-4 text-center mb-4 border border-[#e83e8c]/20">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Código de reserva</p>
                <p className="text-xl font-bold font-mono text-[#e83e8c]">{reserva.codigoUnico}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#e83e8c]" />
                    Datos del viaje
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Origen - Destino:</span>
                      <span className="font-medium text-right">{datosViaje.origen} → {datosViaje.destino}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Fecha:</span>
                      <span className="font-medium text-right">{formatFecha(datosViaje.fecha)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Horario:</span>
                      <span className="font-medium text-right">{datosViaje.horaSalida} - {datosViaje.horaLlegada}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <User className="w-4 h-4 text-[#e83e8c]" />
                    Datos del titular
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Nombre:</span>
                      <span className="font-medium text-right">{reserva.nombrePasajero}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Teléfono:</span>
                      <span className="font-medium text-right">{reserva.telefonoPasajero}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <User className="w-4 h-4 text-[#e83e8c]" />
                    Pasajeros
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Cantidad:</span>
                      <span className="font-medium text-right">{reserva.cantidadPasajeros}</span>
                    </div>
                    {reserva.nombresPasajeros && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Nombres:</span>
                        <span className="font-medium text-right">{reserva.nombresPasajeros}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 flex justify-between items-center border-b border-gray-200">
                  <span className="text-sm text-gray-500">Método de pago</span>
                  <span className="font-medium text-gray-800">{reserva.metodoPago || 'No especificado'}</span>
                </div>

                <div className="bg-white px-4 py-4 flex justify-between items-center">
                  <span className="text-sm text-gray-500">{getLabelTotal()}</span>
                  <div className="text-right">
                    {reserva.monedaMetodoPago === 'VES' && (
                      <>
                        <div className="text-2xl font-bold text-[#e83e8c]">
                          Bs {formatNumero(conversion.bolivares, 2)}
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          ≈ ${formatPrecio(reserva.total)} USD
                        </div>
                      </>
                    )}
                    {reserva.monedaMetodoPago === 'BRL' && (
                      <>
                        <div className="text-2xl font-bold text-[#e83e8c]">
                          R$ {formatNumero(conversion.reales, 2)}
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          ≈ ${formatPrecio(reserva.total)} USD
                        </div>
                      </>
                    )}
                    {reserva.monedaMetodoPago !== 'VES' && reserva.monedaMetodoPago !== 'BRL' && (
                      <div className="text-2xl font-bold text-[#e83e8c]">
                        ${formatPrecio(reserva.total)}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <button
                onClick={descargarPDF}
                disabled={descargando}
                className="w-full mt-4 bg-white border-2 border-[#e83e8c] text-[#e83e8c] hover:bg-[#e83e8c] hover:text-white py-3 rounded-xl text-sm font-medium transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {descargando ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-current"></div>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Descargar comprobante PDF
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </PublicLayout>
  );
};

export default BuscarReserva;